import { Row } from "@tanstack/react-table";
import { VariantProps } from "class-variance-authority";
import { FileText, PencilLine, Trash2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { useProductDetailContext } from "../../context/ProductDetailContext";

import { useProductItemTableColumns } from "./ProductItemTableColumns";

import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { badgeVariants } from "@/components/ui/badge";
import {
  DELETE_CONFIRMATION_CONSTANTS,
  REQUEST_METHODS,
} from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { Permissions } from "@/interfaces/globals";
import {
  IProductLogsItem,
  ISubProductItem,
  RequestStatus,
  SubProductItemStatus,
  SubProductStatus,
} from "@/interfaces/tools.interface";
import { useMutateSubProductItemMutation } from "@/redux/api/toolsQuery";
import { showAlertDialog } from "@/redux/slices/AlertSlice";
import { hasWritePermission } from "@/redux/slices/permissionSlice";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";

export const getBadgeVariant = (
  status: string,
): VariantProps<typeof badgeVariants>["variant"] => {
  switch (status) {
    case SubProductStatus.AVAILABLE:
    case RequestStatus.ACCEPTED:
      return "lightSuccessWithBorder";
    case SubProductStatus.SENT_TO_SITE:
      return "purpleWithBorder";
    case SubProductStatus.ON_SITE:
      return "skyBlueWithBorder";
    case SubProductStatus.USED:
      return "blueWithBorder";
    case SubProductStatus.DAMAGED:
    case RequestStatus.REJECTED:
      return "lightDestructiveWithBorder";
    case SubProductStatus.LOST:
      return "greyWithBorder";
    case SubProductStatus.UNDER_REPAIR:
      return "pinkWithBorder";
    case RequestStatus.PENDING:
      return "yellowWithBorder";
    default:
      return "lightSuccessWithBorder";
  }
};
interface SubProductItemsTableProps {
  data: ISubProductItem[];
  toggleAddSubProductItemModal: () => void;
  toggleEditSubProductItemModal: () => void;
  toggleViewLogItemSheet: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      itemName: string;
      data: IProductLogsItem[];
    } | null>
  >;
}

const SubProductItemsTable = ({
  data,
  toggleAddSubProductItemModal,
  toggleEditSubProductItemModal,
  toggleViewLogItemSheet,
}: SubProductItemsTableProps): JSX.Element => {
  const dispatch = useDispatch();
  const { productId } = useProductDetailContext();
  const { id: sub_product_id } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);
  const { set } = useQueryID(ToolsKeys.SUB_PRODUCT_ITEM_ID);
  const { ITEMS } = ToolsConstants.FORM;

  const [mutateSubProductItem] = useMutateSubProductItemMutation();

  const handleStatusMutation = async (
    row: Row<ISubProductItem>,
    status: SubProductItemStatus,
  ): Promise<void> => {
    if (row.original.status === status) {
      customToast.error(ITEMS.ITEM_STATUS_ALREADY_SET);

      return;
    }
    const response = await mutateSubProductItem({
      method: REQUEST_METHODS.PUT,
      productId,
      subProductId: sub_product_id,
      itemId: row.original.id,
      data: {
        status,
      },
    });
    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(ITEMS.UPDATED_ITEMS_SUCCESS);
      },
    });
  };
  const handleDelete = (id: string): void => {
    dispatch(
      showAlertDialog({
        title: DELETE_CONFIRMATION_CONSTANTS.title,
        desc: ITEMS.ITEM_DELETE_CONFIRMATION,
        btnName: DELETE_CONFIRMATION_CONSTANTS.btnName,
        cancelName: DELETE_CONFIRMATION_CONSTANTS.cancelName,
        btnAction: async (): Promise<void> => {
          const response = await mutateSubProductItem({
            method: REQUEST_METHODS.DELETE,
            productId,
            subProductId: sub_product_id,
            itemId: id,
          });
          validateResponse({
            response,
            onSuccess: () => {
              customToast.success(ITEMS.DELETED_ITEMS_SUCCESS);
            },
          });
        },
      }),
    );
  };

  const getActionOptions = (
    row: Row<ISubProductItem>,
  ): ActionButtonDataTypes[] => [
    {
      icon: FileText,
      name: "View Log",
      onclick: (): void => {
        toggleViewLogItemSheet({
          isOpen: true,
          itemName: row.original.name,
          data: row.original.logs,
        });
      },
    },
    {
      icon: PencilLine,
      name: "Edit",
      onclick: (): void => {
        set(row.original.id);
        toggleEditSubProductItemModal();
      },
    },
    {
      icon: Trash2,
      name: "Move to Trash",
      isTrash: true,
      onclick: () => handleDelete(row.original.id),
    },
  ];

  const writePermissions = useSelector(hasWritePermission(Permissions.TOOLS));

  const columns = useProductItemTableColumns({
    getActionOptions,
    writePermissions,
    handleStatusMutation,
  });

  return (
    <div>
      <CustomTable
        title={ToolsConstants.PRODUCTS.ITEMS}
        columns={columns}
        data={data}
        empty={{}}
        hide={{
          functions: true,
          filters: true,
        }}
        hasWritePermission={writePermissions}
        functions={{
          isExport: true,
          columnToggle: true,
          add: {
            label: ToolsConstants.FORM.ITEMS.ADD_ITEM,
            handleClick: toggleAddSubProductItemModal,
          },
          RightClickData: (
            row: GlobalRowDataTypes<ISubProductItem>,
          ): ActionButtonDataTypes[] =>
            getActionOptions(row as Row<ISubProductItem>),
        }}
      />
    </div>
  );
};

export default SubProductItemsTable;
